/**
 * DECISIONS 415 step 4 (ARCHITECTURE §5.52 (3)): the one primitive every bot decision is to be made of — a **choice** over
 * an ordered list of **options**, each with a **gate** (may it be chosen at all), a **score** (its named terms and their
 * total) and the pick: the highest total, the previous pick's **inertia** added, ties to the earlier option (or to the
 * `start` option, which a chain's default stands as). Pure and deterministic: the options in a fixed order, scored in it.
 *
 * - `weigh` sums named terms by weights left to right — `w₀·t₀ + w₁·t₁ + …`, a penalty a negative term — the arithmetic a
 *   hand-written score does, in its order, so a scorer moved onto it keeps its every bit.
 * - A **trace** (off unless a sink is set): each choice can hand `{ who, tick, choice, options, pick }` to a sink that tests
 *   and a Lab "why" view read — the scored options with their terms and whether their gate held. Nothing is built
 *   when no sink is set.
 *
 * Today's users: ai/actionJobs.ts chooseJob (the utility brain's job) and ai/actionPositions.ts chooseFightSpot (B1's
 * fight spot). The leader's step, the commander's assignment and the soldier's activity follow (§5.52 steps 5–7).
 */

/** A term's name and its value (a penalty is negative). */
export type Term = readonly [name: string, value: number];

/** `w₀·t₀ + w₁·t₁ + …` in the terms' order; every term needs its weight (a missing one is NaN, as a hand-written sum's would be). The first product starts the sum (no `0 +`). */
export function weigh(terms: readonly Term[], weights: Readonly<Record<string, number>>): number {
  let total = 0;
  for (let i = 0; i < terms.length; i++) {
    const [name, value] = terms[i];
    const w = weights[name];
    total = i === 0 ? w * value : total + w * value;
  }
  return total;
}

/** One option of a choice: its kind, whether it may be chosen, and its score. */
export interface ChoiceOption<K, C> {
  kind: K;
  /** May it be chosen now (absent: always)? A gated-out option is not scored. */
  gate?: (ctx: C) => boolean;
  /** Its total, with the named terms that made it (for the trace; empty when none are kept). */
  score: (ctx: C) => { total: number; terms?: readonly Term[] };
}

/** An option as scored, for the trace. */
export interface ScoredOption<K> { kind: K; gated: boolean; total: number; terms: readonly Term[] }

/** What a trace sink receives: who chose, when, which choice, the options as scored, the pick. */
export interface ChoiceTraceEntry { who: number; tick: number; choice: string; options: ScoredOption<unknown>[]; pick: unknown }
export type ChoiceTrace = (entry: ChoiceTraceEntry) => void;

export interface ChooseOpts<K> {
  /** The choice's name (for the trace). */
  name?: string;
  /** The previous pick, which gets `inertia` added (no flicker). */
  prev?: K | null;
  inertia?: number;
  /** The index of the option that stands until another scores strictly more (default the first that passes its gate). */
  start?: number;
  trace?: { sink: ChoiceTrace; who: number; tick: number };
}

/**
 * The pick: the option with the highest total (its inertia added if it was the previous pick); the earlier wins a tie,
 * except the `start` option, which only a strictly higher total displaces. Null (index −1) when every option is gated out.
 */
export function choose<K, C>(options: readonly ChoiceOption<K, C>[], ctx: C, opts: ChooseOpts<K> = {}): { pick: K | null; index: number; total: number } {
  const traced: ScoredOption<K>[] | null = opts.trace ? [] : null;
  const open: boolean[] = [], totals: number[] = [];
  for (const o of options) {
    const ok = !o.gate || o.gate(ctx);
    open.push(ok);
    if (!ok) { totals.push(0); traced?.push({ kind: o.kind, gated: true, total: 0, terms: [] }); continue; }
    const s = o.score(ctx);
    const total = opts.prev !== undefined && opts.prev !== null && o.kind === opts.prev ? s.total + (opts.inertia ?? 0) : s.total;
    totals.push(total);
    traced?.push({ kind: o.kind, gated: false, total, terms: s.terms ?? [] });
  }
  let best = opts.start !== undefined && open[opts.start] ? opts.start : -1;
  for (let i = 0; i < options.length; i++) {
    if (!open[i]) continue;
    if (best < 0 || totals[i] > totals[best]) best = i;
  }
  const pick = best < 0 ? null : options[best].kind;
  if (opts.trace) opts.trace.sink({ who: opts.trace.who, tick: opts.trace.tick, choice: opts.name ?? '', options: traced!, pick });
  return { pick, index: best, total: best < 0 ? 0 : totals[best] };
}

/** One option of a tier-ordered choice: its kind, whether it holds now, and what it gives when it does. */
export interface TierOption<K, R> {
  kind: K;
  /** Does it hold now? Asked in tier order; the options after the first that holds are never asked. */
  when: () => boolean;
  /** What it gives (asked only when it is the pick). */
  give: () => R;
}

/**
 * A first-match chain as a choice: the options in tier order, the first whose `when` holds is the pick and gives the
 * result — exactly an if-chain (each `when` asked in order, the rest never), so a chain moved onto it keeps its every
 * bit. The trace records the options asked (each held or not) and the pick. The fallback is the last option, whose
 * `when` should hold always.
 */
export function chooseFirst<K, R>(options: readonly TierOption<K, R>[], opts: { name?: string; trace?: { sink: ChoiceTrace; who: number; tick: number } } = {}): R {
  const asked: ScoredOption<K>[] | null = opts.trace ? [] : null;
  for (const o of options) {
    const holds = o.when();
    asked?.push({ kind: o.kind, gated: !holds, total: holds ? 1 : 0, terms: [] });
    if (!holds) continue;
    if (opts.trace) opts.trace.sink({ who: opts.trace.who, tick: opts.trace.tick, choice: opts.name ?? '', options: asked!, pick: o.kind });
    return o.give();
  }
  throw new Error(`chooseFirst ${opts.name ?? ''}: no option held (the last should always hold)`);
}
